// src/opencomponents/MusicPlayer.js — V18 player.
//
// Fixed bottom-left. Expanded on the home page; on every other page it
// collapses to a 64px visceral orb so it never covers content. Audio logic
// (session persistence, autoplay fallback, analyser -> window.__voidAudioLevel)
// is carried over from the previous player; dragging/edge-hiding is gone.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useLocation } from 'react-router-dom';
import { color, font } from '../theme/tokens';

// --- 播放列表数据 (保持不变) ---
const disc1_files = [
    '01 音に出来る事.mp3', '02 夜の向日葵.mp3', '03 変わらないからこそ.mp3', '04 OMOSHIRO MUSIC.mp3',
    '05 Happy Material.mp3', '06 夢見る魚 -Erik Satie-.mp3', '07 リビングゲーム.mp3', '08 不安の立像.mp3',
    '09 FURYOUミュージック.mp3', '10 電波リレーの勝者.mp3', '11 ピカデリー -Erik Satie-.mp3', '12 言葉と旋律.mp3',
    '13 Les Enfants du Paradis.mp3', '14 beautiful everyday.mp3', '15 邂逅.mp3', '16 小さな旋律.mp3',
    '17 風の吹く丘.mp3', '18 組曲の行方.mp3', '19 ナグルファルの船上にて -Orgel Ver.-.mp3', '20 夏の大三角.mp3'
];

const disc2_files = [
    '01 空気力学少女と少年の詩 -Oegel Ver.-.mp3', '02 平坦、そして美しき日常.mp3', '03 見慣れた風景.mp3',
    '04 怪優奇優侏儒巨人美少女等募集.mp3', '05 updowncity.mp3', '06 電波と電磁波の関係.mp3',
    '07 主よ、人の望みの喜びよ.mp3', '08 終ワリノ旋律.mp3', '09 なぜ日は傾くのか.mp3', '10 人よ、幸福に！.mp3',
    '11 小さな記憶.mp3', '12 同じ空はどこにも無い.mp3', '13 窓と光.mp3', '14 黒い翼の舞.mp3',
    '15 少年とナイフ.mp3', '16 Tractatus Logico-philosophicus.mp3', '17 空気力学少女と少年の詩 -Piano Ver.-.mp3',
    '18 最果ての空.mp3', '19 KAROYAKAGirls.mp3'
];

const formatTitle = (filename) => {
    return filename.replace('.mp3', '').replace(/^\d+\s*/, '');
};

const playlist = [
    ...disc1_files.map(file => ({ title: formatTitle(file), src: `/music/disc1/${file}` })),
    ...disc2_files.map(file => ({ title: formatTitle(file), src: `/music/disc2/${file}` }))
];
// --- 播放列表数据结束 ---

// --- 缓存 Key ---
const PLAYER_STATE_KEY = 'musicPlayerState';

const Dock = styled.div`
    position: fixed;
    left: 28px;
    bottom: 28px;
    z-index: 90;
    @media (max-width: 720px) { left: 12px; bottom: 12px; }
`;

const Bar = styled.div`
    box-sizing: border-box;
    height: 64px;
    width: ${({ $open }) => ($open ? '420px' : '64px')};
    max-width: calc(100vw - 24px);
    padding: ${({ $open }) => ($open ? '0 8px 0 9px' : '0 9px')};
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(15, 5, 9, 0.9);
    border: 1px solid ${color.line};
    backdrop-filter: blur(10px);
    overflow: hidden;
    transition: width 0.35s ease;
`;

const OrbButton = styled.button`
    all: unset;
    cursor: pointer;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    &:focus-visible { outline: 2px solid ${color.bloodHi}; outline-offset: 3px; }
`;

const Orb = styled.div`
    position: relative;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    box-shadow: 0 0 18px rgba(200, 50, 62, 0.55);
    ${({ $playing }) => $playing && css`animation: ts-beat 1.3s ease-out infinite;`}
    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: url('/resource/flesh-orb.webp') center / cover no-repeat;
        animation: ts-spin 28s linear infinite;
        animation-play-state: ${({ $playing }) => ($playing ? 'running' : 'paused')};
    }
`;

const Info = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-grow: 1;
    min-width: 0;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transition: opacity 0.25s ease;
`;

const Title = styled.div`
    font-family: ${font.display};
    font-size: 14px;
    color: ${color.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Meta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: ${font.mono};
    font-size: 11px;
    color: ${color.text3};
    letter-spacing: 0.08em;
`;

const Meter = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 16px;
    span {
        width: 3px;
        height: 5%;
        background: ${color.denpa};
        transition: height 0.08s linear;
    }
`;

const Controls = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

const Ctl = styled.button`
    all: unset;
    cursor: pointer;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${color.text2};
    transition: color 0.2s ease;
    &:hover { color: ${color.text}; }
    &:focus-visible { outline: 2px solid ${color.bloodHi}; outline-offset: -4px; }
`;

const trackLabel = (i) => {
    const disc = i < disc1_files.length ? 1 : 2;
    const n = disc === 1 ? i + 1 : i - disc1_files.length + 1;
    const total = disc === 1 ? disc1_files.length : disc2_files.length;
    return `DISC ${disc} · ${String(n).padStart(2, '0')} / ${total}`;
};

const MusicPlayer = () => {
    const initialStateRef = useRef((() => {
        try {
            const saved = sessionStorage.getItem(PLAYER_STATE_KEY);
            if (saved) return JSON.parse(saved);
        } catch (e) {
            console.error('无法从 sessionStorage 解析状态:', e);
        }
        return {
            currentTrackIndex: Math.floor(Math.random() * playlist.length),
            isPlaying: true,
            currentTime: 0,
        };
    })());

    const { pathname } = useLocation();
    const isHome = pathname === '/';
    const [currentTrackIndex, setCurrentTrackIndex] = useState(initialStateRef.current.currentTrackIndex);
    const [isPlaying, setIsPlaying] = useState(initialStateRef.current.isPlaying);
    const [openOverride, setOpenOverride] = useState(null);
    const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 720px)').matches);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 720px)');
        const on = () => setNarrow(mq.matches);
        mq.addEventListener('change', on);
        return () => mq.removeEventListener('change', on);
    }, []);
    const open = openOverride ?? (isHome && !narrow);

    const audioRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const frameRef = useRef(0);
    const initialTimeSetRef = useRef(false);
    const meterRef = useRef(null);

    // back to the route's default whenever the page changes
    useEffect(() => { setOpenOverride(null); }, [pathname]);

    // persistence across reloads within the session
    useEffect(() => {
        const save = () => {
            try {
                sessionStorage.setItem(PLAYER_STATE_KEY, JSON.stringify({
                    currentTrackIndex,
                    currentTime: audioRef.current ? audioRef.current.currentTime : 0,
                    isPlaying,
                }));
            } catch (e) {
                console.error('无法保存播放器状态:', e);
            }
        };
        window.addEventListener('beforeunload', save);
        return () => window.removeEventListener('beforeunload', save);
    }, [currentTrackIndex, isPlaying]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return undefined;
        const onMeta = () => {
            if (initialStateRef.current.currentTime > 0 && !initialTimeSetRef.current) {
                audio.currentTime = initialStateRef.current.currentTime;
                initialTimeSetRef.current = true;
            }
        };
        audio.addEventListener('loadedmetadata', onMeta);
        return () => audio.removeEventListener('loadedmetadata', onMeta);
    }, []);

    // play / pause; browsers may refuse autoplay until the first gesture
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) {
            if (!audioContextRef.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                const context = new Ctx();
                const source = context.createMediaElementSource(audio);
                const analyser = context.createAnalyser();
                analyser.fftSize = 256;
                source.connect(analyser);
                analyser.connect(context.destination);
                audioContextRef.current = context;
                analyserRef.current = analyser;
            }
            audioContextRef.current.resume();
            const p = audio.play();
            if (p !== undefined) {
                p.catch((err) => {
                    console.error('浏览器策略阻止了自动播放:', err);
                    setIsPlaying(false);
                });
            }
        } else {
            audio.pause();
        }
    }, [isPlaying]);

    const firstTrackRef = useRef(true);
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (firstTrackRef.current) { firstTrackRef.current = false; return; }
        audio.load();
        initialTimeSetRef.current = false;
        if (isPlaying) audio.play().catch((e) => console.error('切歌后自动播放失败:', e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTrackIndex]);

    const playNext = useCallback(() => setCurrentTrackIndex((i) => (i + 1) % playlist.length), []);
    const playPrev = useCallback(() => setCurrentTrackIndex((i) => (i - 1 + playlist.length) % playlist.length), []);
    const togglePlay = useCallback(() => setIsPlaying((v) => !v), []);

    // level meter + audio level for the WebGL layers
    useEffect(() => {
        const bars = meterRef.current ? Array.from(meterRef.current.children) : [];
        const reset = () => {
            window.__voidAudioLevel = 0;
            bars.forEach((b) => { b.style.height = '5%'; });
        };
        if (!isPlaying || !open) { reset(); return undefined; }
        const data = new Uint8Array(128);
        const draw = () => {
            frameRef.current = requestAnimationFrame(draw);
            const an = analyserRef.current;
            if (!an) return;
            an.getByteFrequencyData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i++) sum += data[i];
            window.__voidAudioLevel = sum / data.length / 255;
            const step = Math.floor(data.length / (bars.length + 1));
            bars.forEach((b, i) => { b.style.height = `${Math.max((data[(i + 1) * step] / 255) * 100, 5)}%`; });
        };
        draw();
        return () => { cancelAnimationFrame(frameRef.current); reset(); };
    }, [isPlaying, open]);

    const track = playlist[currentTrackIndex];

    return (
        <Dock>
            <audio ref={audioRef} src={track.src} onEnded={playNext} crossOrigin="anonymous" />
            <Bar $open={open}>
                <OrbButton
                    type="button"
                    onClick={() => setOpenOverride(!open)}
                    aria-label={open ? '收起播放器' : `展开播放器：${track.title}`}
                    aria-expanded={open}
                >
                    <Orb $playing={isPlaying} />
                </OrbButton>
                {open && (
                    <>
                        <Info $open={open}>
                            <Title title={track.title}>{track.title}</Title>
                            <Meta>
                                <span>{trackLabel(currentTrackIndex)}</span>
                                <Meter ref={meterRef} aria-hidden="true">
                                    <span /><span /><span /><span /><span />
                                </Meter>
                            </Meta>
                        </Info>
                        <Controls>
                            <Ctl type="button" onClick={playPrev} aria-label="上一首">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true"><path d="M4 3v10M13 3 6 8l7 5z" /></svg>
                            </Ctl>
                            <Ctl type="button" onClick={togglePlay} aria-label={isPlaying ? '暂停' : '播放'}>
                                {isPlaying ? (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M4 2.5h3v11H4zM9 2.5h3v11H9z" /></svg>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M4 2.5v11l9.5-5.5z" /></svg>
                                )}
                            </Ctl>
                            <Ctl type="button" onClick={playNext} aria-label="下一首">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true"><path d="M12 3v10M3 3l7 5-7 5z" /></svg>
                            </Ctl>
                        </Controls>
                    </>
                )}
            </Bar>
        </Dock>
    );
};

export default MusicPlayer;
